import React, { useCallback, useState } from 'react';
import {
  createSLHManager,
  editSLHManager,
  deactivateSLHManager,
  activateSLHManager,
  resendActivationInvitation,
} from 'api/api';
import {
  SelectOption,
  OrangeButton,
  IconSmallArrow,
  FormInput,
  IconSoberLiving,
  IconInfo,
  SmallButton,
} from 'components';
import { COLORS } from 'utils/colors';
import { Form, Tooltip } from 'antd';
import GlobalModal from 'components/globalModal';
import { ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { useAuth } from 'context/authContext';
import { allowOnlyDigits } from 'utils/helpers';
import SlhSelect from 'components/slhSelect';

export const AddEditPFManagerModal = ({
  adminData,
  onSuccess,
  onClose,
  isOpen,
  isEditMode,
  partnerID,
  role,
  partners,
  partnerDetails,
}) => {
  const { isParentManaging } = useAuth();
  const [formData, setFormData] = useState({
    firstname: isEditMode ? adminData?.firstname : '',
    lastname: isEditMode ? adminData?.lastname : '',
    email: isEditMode ? adminData?.email : '',
    phone: isEditMode ? adminData?.phone : '',
    country_code: isEditMode ? adminData?.country_code : '+1',
    partnerId: isEditMode ? adminData?.partner?.id : partnerID || '',
    gender: '',
  });

  const onValuesChange = (_, allValues) => {
    const { firstname, lastname, email, phone } = allValues;

    setFormData({ ...formData, firstname, lastname, email, phone });
  };

  const handleFormSubmit = useCallback(() => {
    if (isEditMode) {
      editSLHManager(
        {
          ...formData,
          partnerId: formData.partnerId,
          phone: formData.phone,
          type: 'manager',
        },
        role,
        adminData?.id,
        () => {
          onClose();
          onSuccess();
        },
      );
    } else {
      createSLHManager(
        {
          ...formData,
          partnerId: formData.partnerId,
          phone: formData.phone,
          type: 'manager',
        },
        role,
        () => {
          onClose();
          onSuccess();
        },
      );
    }
  }, [adminData?.id, formData, isEditMode, onClose, onSuccess, role]);

  const handleAdminTypeChange = (value) => {
    setFormData({
      ...formData,
      type: value,
    });
  };
  const handlePartnerSelectChange = (value) => {
    setFormData({
      ...formData,
      partnerId: value,
    });
  };

  const handlePF25AdminStatusChange = useCallback(() => {
    if (adminData?.status === 'active') {
      return (
        <SmallButton
          btnClassName="red"
          label="Deactivate"
          onClick={() => {
            deactivateSLHManager(role, adminData?.id, () => {
              onClose();
              window.location.reload();
            });
          }}
        />
      );
    }

    if (adminData?.status === 'pending') {
      return (
        <SmallButton
          btnClassName="orange"
          label="Resend"
          onClick={() => {
            resendActivationInvitation(role, adminData?.id, () => {
              onClose();
            });
          }}
        />
      );
    }
    return (
      <SmallButton
        btnClassName="green"
        label="Activate"
        onClick={() => {
          activateSLHManager(role, adminData?.id, () => {
            onClose();
            window.location.reload();
          });
        }}
      />
    );
  }, [adminData?.id, adminData?.status, onClose, role]);

  const sanitizeDigitsOnInput = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const digitsOnly = rawValue.replace(/\D/g, '');

    const prefixDigits = formData.country_code.replace(/\D/g, '');
    const sanitized = digitsOnly.startsWith(prefixDigits)
      ? digitsOnly.slice(prefixDigits.length)
      : digitsOnly;

    if (rawValue !== sanitized) {
      input.value = sanitized;
    }
  };

  return (
    <GlobalModal
      width={510}
      open={isOpen}
      title={`${isEditMode ? 'Edit' : 'Add'} SLH Manager`}
      onClose={onClose}
      titleLeft
    >
      <Form
        requiredMark={'optional'}
        name="terminate_form"
        className="flex flex-col p-[30px] gap-y-6"
        initialValues={{ ...formData }}
        onValuesChange={onValuesChange}
        onFinish={handleFormSubmit}
      >
        <FormInput
          label="Name*"
          name="firstname"
          placeholder="First Name"
          defaultValue={formData.firstname}
          required
        />
        <FormInput
          label="Last Name*"
          name="lastname"
          defaultValue={formData.lastname}
          placeholder="Last Name"
          required
        />
        <FormInput
          className="w-full"
          type={'email'}
          defaultValue={formData.email}
          disabled={isEditMode}
          label={
            <div className="flex ">
              <p>Email*</p>
              <Tooltip
                className="ml-1"
                title="This email will be used as SLH Managed Login credential"
                overlayInnerStyle={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '16px',
                  fontFamily: 'poppins',
                  backgroundColor: '#000',
                }}
                placement="top"
              >
                <IconInfo color={COLORS.blue} />
              </Tooltip>
            </div>
          }
          name="email"
          placeholder="Email"
          required
        />
        <FormInput
          label="Phone"
          defaultValue={formData.phone}
          name="phone"
          type="text"
          pattern="['0-9']*"
          maxLength={10}
          placeholder="Phone"
          prefix={formData.country_code || '+1'}
          onKeyDown={allowOnlyDigits}
          onWheel={(e) => {
            e.target.blur();
            e.stopPropagation();

            setTimeout(() => {
              e.target.focus();
            }, 0);
          }}
          onInput={sanitizeDigitsOnInput}
        />
        <div className="flex flex-col w-full gap-y-1">
          <p className="text-14 font-medium">Type</p>
          <SelectOption
            onSelect={(value) => handleAdminTypeChange(value)}
            dropdownStyle={{ padding: '16px', background: 'white' }}
            required
            suffixIcon={<IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />}
            className="h-[44px] text-base font-medium base-block rounded-lg"
            placeholder="Type"
            defaultValue={partnerID || role === ROLE_TYPES.admin ? '2' : '1'}
            dropdownRender={(originalNode) => originalNode}
            disabled
            optionRender={(option) => (
              <div
                className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${formData.type.toLowerCase() === option.label.toLowerCase() ? 'border-orange' : ''}`}
              >
                <span
                  className={`w-6 h-6  rounded-full border border-orange ${formData.type.toLowerCase() === option.label.toLowerCase() ? 'border-[6px]' : ''}`}
                />
                <IconSoberLiving />
                <p className="font-14">{option.label}</p>
              </div>
            )}
            options={[
              { label: 'Admin', value: '1' },
              { label: 'Manager', value: '2' },
            ]}
          />
        </div>
        <div className="flex flex-col w-full gap-y-1">
          <p className="text-14 font-medium">SLH</p>
          <SlhSelect
            data={partners}
            value={formData.partnerId}
            onChange={(id) => handlePartnerSelectChange(id)}
            placement="top"
            disabled={
              isEditMode ||
              (!isParentManaging &&
                partnerDetails?.type !== SLH_TYPES.parent &&
                role !== ROLE_TYPES.admin)
            }
          />
        </div>
        <div className="flex justify-between items-center w-full">
          {isEditMode && handlePF25AdminStatusChange()}
          <OrangeButton
            type="submit"
            label={`${isEditMode ? 'Save' : 'Add'}`}
            className="self-end"
          />
        </div>
      </Form>
    </GlobalModal>
  );
};
